import React, { useState, useEffect, useMemo } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";

import CardSection from "../../../components/CardSection/CardSection";
import { setVersData, clearVers } from "./VersementSlice";
import { selectActiveUid } from "../../Auth/authSlice";
import {
  getKinshasaDateTime,
  getKinshasaTimeHMS,
} from "../../../utils/getKinshasaDateTime";
import { db, auth } from "../../../config/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

import { doc, setDoc, getDoc } from "firebase/firestore";
import MyDatePicker from "../../../components/CardSection/MyDatePicker";
import { getYesterdayIso } from "../../../utils/dateUtils";
import { getKinshasaDate } from "../../../utils/getKinshasaDateTime";

const Versement = ({ title = "VERSEMENTS", listItems = null }) => {
  const [verString, setVerString] = useState(""); // For "ver" field (e.g., "OIL_SHOP_QC_FRETIN")
  const [vers, setVers] = useState();
  const [dateVers, setDateVers] = useState(() => getYesterdayIso());
  const myVersStore = useSelector((state) => state.vers);
  const [selectedVers, setSelectedVers] = useState("");
  const [cardOpen, setCardOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch();
  const activeUid = useSelector(selectActiveUid);

  useEffect(() => {
    let canceled = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (!canceled) setVerString("");
        return;
      }
      try {
        // Read "ver" field from users/{uid} document
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const verValue = userDocSnap.data().ver || "";
          if (!canceled) setVerString(verValue);
        }
      } catch (e) {
        console.error("load vers failed:", e);
      }
    });
    return () => {
      canceled = true;
      unsub();
    };
  }, []);

  // Parse "ver" string to list of versement types
  // e.g., "OIL_SHOP_QC_FRETIN" → [{label: "OIL_CDF", value: 1}, {label: "OIL_USD", value: 2}, ...]
  const listFromVerString = useMemo(() => {
    if (!verString) return [];
    const tokens = verString.split("_"); // ["OIL", "SHOP", "QC", "FRETIN"]
    const result = [];
    let order = 1;
    for (const token of tokens) {
      result.push({ label: `${token}_CDF`, value: order++ });
      result.push({ label: `${token}_USD`, value: order++ });
    }
    return result;
  }, [verString]);

  // Determine effective list: listItems prop > listFromVerString
  const effectiveList = listItems || listFromVerString;

  // Hide component if list is empty
  if (!effectiveList || effectiveList.length === 0) {
    return null;
  }

  const handleSelectedVersChange = (event) => {
    setSelectedVers(event.target.value);
  };

  const handleChangeVers = (event) => {
    setVers(event.target.value);
  };

  const handleSelectedDateChange = (e) => {
    setDateVers(e.target.value); // "2025-11-14"
    setError("");
  };

  const handleAdd = () => {
    if (!selectedVers || !vers) return;

    const date = getKinshasaDateTime();
    const payload = {
      type: "VERS",
      item: selectedVers,
      value: vers,
      dateVers: dateVers,
      date: date,
    };

    dispatch(setVersData(payload));

    setSelectedVers("");
    setVers("");
    // Do not close here; will close after Firestore write in onClick
  };

  const onClick = async (e) => {
    e.stopPropagation();
    setLoading(true);
    setError("");
    try {
      // Doc ID in users/{uid}/versements is deterministic ({item}_{dateVers}),
      // so a second submission for a date already declared this month would
      // silently overwrite it there (montant changes but a stale
      // verified/bankMontant from the earlier reconciliation stays attached).
      // Block it up front instead — corrections go through Admin Correction.
      const [yyyy, mm, dd] = dateVers.split("-");
      const dateVersFormatted = `${dd}-${mm}-${yyyy}`;
      const items = Object.keys(myVersStore.vers);
      const existingChecks = await Promise.all(
        items.map((item) =>
          getDoc(doc(db, "users", activeUid, "versements", `${item}_${dateVersFormatted}`)),
        ),
      );
      const duplicateItem = items.find((_, i) => existingChecks[i].exists());
      if (duplicateItem) {
        setError(
          `Un versement existe déjà pour ${duplicateItem} à la date du ${dateVersFormatted}. Utilisez la correction admin pour le modifier.`,
        );
        setLoading(false);
        return;
      }

      const date = getKinshasaDate();
      const docRef = doc(db, "users", activeUid, "data", date);
      const timeKey = getKinshasaTimeHMS();

      // Any configured item (from the station's "ver" config) the pompiste
      // didn't fill in gets declared as 0 — an explicit "nothing to report"
      // record instead of just silently missing, so Validation can tell
      // "declared zero" apart from "never declared at all".
      const dateTime = getKinshasaDateTime();
      const fullVersData = { ...myVersStore.vers };
      for (const { label: item } of effectiveList) {
        if (!fullVersData[item]) {
          fullVersData[item] = {
            type: "VERS",
            item,
            value: "0",
            dateVers,
            date: dateTime,
          };
        }
      }

      // Build payload with timestamped keys
      const payload = {};
      for (const [item, data] of Object.entries(fullVersData)) {
        const key = `${item}_${timeKey}`;
        payload[key] = data;
      }

      await setDoc(docRef, payload, { merge: true });
      console.log("Document written with ID: ");
      setCardOpen(false); // Close CardSection after successful Firestore write
    } catch (e) {
      console.error("Error adding document: ", e);
    }

    dispatch(clearVers());
    setLoading(false);
  };

  return loading ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: 32,
      }}
    >
      <CircularProgress color="primary" size={48} />
      <div style={{ marginTop: 16, fontSize: 18 }}>Chargement...</div>
    </div>
  ) : (
    <CardSection
      title={title}
      myStore={myVersStore.vers}
      listItem={effectiveList}
      handleSelectedItemChange={handleSelectedVersChange}
      selectedItem={selectedVers}
      handleChangeValue={handleChangeVers}
      handleAdd={handleAdd}
      selectedValue={vers}
      onClick={onClick}
      open={cardOpen}
      setOpen={setCardOpen}
    >
      <MyDatePicker value={dateVers} onChange={handleSelectedDateChange} />
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
    </CardSection>
  );
};

export default Versement;
